import { useEditorStore } from '@/store/use-editor-store';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { EventType, emitter } from '@/utils/emitter.utils';
import { debounce, round } from 'lodash-es';
import {
  computed,
  onActivated,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch
} from 'vue';
import { useEmitterListener } from './use-event-listener';
import { useGlobalShortcuts } from './use-global-shortcuts';
import { getWatchfaceDeviceRadius } from './use-watchface-devices';
import showToast from '@/components/common/toast';
import { useDataSource } from './use-data-source';

let toast: { destroy: () => void } | undefined = undefined;

const info = (message: string, duration = 1500) => {
  toast?.destroy();
  toast = showToast(message, { duration });
};

export const useWorkbench = () => {
  const SCALE_MAX = 2.0;
  const SCALE_MIN = 0.5;

  const watchfaceStore = useWatchfaceStore();
  const editStore = useEditorStore();

  const workBenchRef = ref<HTMLDivElement | null>();
  const canvasRef = ref<HTMLCanvasElement>();

  const konvaSize = reactive({
    width: 0,
    height: 0
  });

  const watchfaceRaduis = computed(() => {
    return (
      getWatchfaceDeviceRadius(watchfaceStore.deviceType ?? '') *
        editStore.watchfaceCanvas.scale +
      'px'
    );
  });

  const _reset = () => {
    // konva size
    const res2 = workBenchRef.value?.getBoundingClientRect();

    konvaSize.width = res2?.width ?? 0;
    konvaSize.height = res2?.height ?? 0;
    emitter.emit(EventType['window:resize']);
  };

  onActivated(() => {
    _reset();
  });

  const reset = debounce(() => {
    _reset();
  }, 50);

  // watch(
  //   () => watchfaceStore.size,
  //   () => {
  //     console.trace('watchfaceStore.description.size');
  //     reset();
  //   }
  // );

  const watchfaceCanvasSet = () => {
    const scale = editStore.watchfaceCanvas.scale ?? 1;
    const width = (watchfaceStore.size.width ?? 466) * scale;
    const height = (watchfaceStore.size.height ?? 466) * scale;
    editStore.watchfaceCanvas.offsetX = konvaSize.width / 2 - width / 2;
    editStore.watchfaceCanvas.offsetY = konvaSize.height / 2 - height / 2;
    const parent = document.getElementById('workbench-outer') as HTMLDivElement;
    parent?.scroll(
      (parent?.scrollWidth - parent?.offsetWidth) / 2,
      (parent?.scrollHeight - parent?.offsetHeight) / 2
    );
  };

  useEmitterListener(EventType['window:resize'], watchfaceCanvasSet);
  useEmitterListener(EventType['watchface:opened'], watchfaceCanvasSet);

  const { expand } = useDataSource();

  watch(expand, debounce(watchfaceCanvasSet, 200));

  onMounted(() => {
    window.addEventListener('resize', reset);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('resize', reset);
  });

  /**
   * 拖拽部分
   */
  const spacePressed = ref(false);
  const posi = reactive({
    x: 0,
    y: 0,
    left: 0,
    top: 0
  });

  // watcface canvas位置
  const handleMouseMove = debounce((e: MouseEvent) => {
    const posi2 = {
      x: e.clientX,
      y: e.clientY
    };

    let offsetX = posi.left + posi2.x - posi.x;
    let offsetY = posi.top + posi2.y - posi.y;

    // 判断阈值
    const scale = editStore.watchfaceCanvas.scale;
    const width = watchfaceStore.size.width * scale;
    const height = watchfaceStore.size.height * scale;

    // 允许的最小值
    offsetX = Math.max((width / 2) * -1, offsetX);
    offsetY = Math.max((height / 2) * -1, offsetY);

    // 允许的最大值
    offsetX = Math.min(konvaSize.width - width / 2, offsetX);
    offsetY = Math.min(konvaSize.height - height / 2, offsetY);

    editStore.watchfaceCanvas.offsetX = offsetX;
    editStore.watchfaceCanvas.offsetY = offsetY;
  }, 5);

  const onDrapMouseDown = (e: MouseEvent) => {
    if (!spacePressed.value || !_haveIntersection(e)) return;

    // clear shape select
    // 记录此刻鼠标位置
    posi.x = e.clientX;
    posi.y = e.clientY;
    posi.left = editStore.watchfaceCanvas.offsetX;
    posi.top = editStore.watchfaceCanvas.offsetY;

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', stopDrag);
  };

  const stopDrag = () => {
    if (workBenchRef.value) workBenchRef.value.style.cursor = 'default';
    window.removeEventListener('mousemove', handleMouseMove);
  };

  // 键盘空格键按下
  onMounted(() => {
    workBenchRef.value?.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.code == 'Space') {
        e.preventDefault();
        if (!spacePressed.value) spacePressed.value = true;
        if (workBenchRef.value) workBenchRef.value.style.cursor = 'grab';
      }
    });
    workBenchRef.value?.addEventListener('keyup', (e) => {
      if (e.code == 'Space') {
        spacePressed.value = false;
        stopDrag();
      }
    });
  });

  const _haveIntersection = (e: MouseEvent) => {
    const { clientX, clientY } = e;
    const {
      width = 0,
      height = 0,
      x = 0,
      y = 0
    } = canvasRef.value?.getBoundingClientRect() ?? {};
    const haveIntersection =
      clientX >= x &&
      clientX <= x + width &&
      clientY >= y &&
      clientY <= y + height;

    return haveIntersection;
  };

  //处理缩放中心
  const handleWatchCanvasCenter = (_scale: number) => {
    const { offsetX, offsetY, scale: preScale } = editStore.watchfaceCanvas;
    const { width, height } = watchfaceStore.size;
    // 实际缩放数据
    const scale = Math.max(SCALE_MIN, Math.min(SCALE_MAX, _scale));

    // 实际缩放的差值
    const _scaleOffset = scale - preScale;

    // 计算鼠标在图形上的坐标
    const mouseX = width / 2;
    const mouseY = height / 2;
    editStore.watchfaceCanvas.scale = scale;
    editStore.watchfaceCanvas.offsetX = round(
      offsetX - _scaleOffset * mouseX,
      0
    );
    editStore.watchfaceCanvas.offsetY = round(
      offsetY - _scaleOffset * mouseY,
      0
    );
  };

  const _step = 0.01;

  // 缩放部分
  const onScaleWheel = (e: WheelEvent) => {
    // 必须同时按住ctrl或command
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    const { scale: preScale } = editStore.watchfaceCanvas;
    const _scale = preScale + _step * (e.deltaY < 0 ? 1 : -1);
    if (_scale <= SCALE_MIN) info('已缩小到最小值');
    else if (_scale >= SCALE_MAX) info('已放大到最大值');
    else info(Math.floor(_scale * 100) + '%');
    handleWatchCanvasCenter(_scale);
  };

  const scaleUp = () => {
    const { scale: preScale } = editStore.watchfaceCanvas;
    const _scale = preScale + _step;
    if (_scale >= SCALE_MAX) info('已放大到最大值');
    else info(Math.floor(_scale * 100) + '%');
    handleWatchCanvasCenter(_scale);
  };

  const scaleDown = () => {
    const { scale: preScale } = editStore.watchfaceCanvas;
    const _scale = preScale - _step;
    if (_scale <= SCALE_MIN) info('已缩小到最小值');
    else info(Math.floor(_scale * 100) + '%');
    handleWatchCanvasCenter(_scale);
  };

  const scaleTo100 = () => {
    info('100%');
    handleWatchCanvasCenter(1);
  };

  useGlobalShortcuts(
    [
      {
        keyboardKeys: ['CommandOrControl', '0'],
        handler: scaleTo100
      },
      {
        keyboardKeys: ['='],
        handler: scaleUp
      },
      {
        keyboardKeys: ['-'],
        handler: scaleDown
      }
    ],
    (e) => {
      const el = e.target as HTMLDivElement;
      return el.dataset['workbench'] == 'true';
    }
  );

  useEmitterListener(EventType['workbench:action'], (data) => {
    const { action } = data ?? {};
    console.log(action);
    switch (action) {
      case 'center':
        reset();
        break;
      case 'scaleUp':
        scaleUp();
        break;
      case 'scaleDown':
        scaleDown();
        break;
      case 'scaleTo100':
        scaleTo100();
        break;
    }
  });

  return {
    workBenchRef,
    konvaSize,
    onDrapMouseDown,
    watchfaceRaduis,
    canvasRef,
    spacePressed,
    onScaleWheel,
    scaleUp,
    scaleDown,
    scaleTo100,
    centerCanvas: reset,
    watchfaceCanvasSet
  };
};
